import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash, Trash2, Copy, Eye, Check } from "lucide-react";
import { getPlanById, createPlan, updatePlan, deletePlan, getPlanes } from '../../services/planesService';
import { getFamilias } from '../../services/familiasService';
import { getUrls } from '../../services/urlsService';
import { deletePlanItem } from '../../services/planItemsService';
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../../components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import { safeLocalStorage, safeParseJson } from '../../lib/storage.js';


const tiposItem = [
  { value: 'titulo', label: 'Título' },
  { value: 'queEs', label: '¿Qué es?' },
  { value: 'beneficio', label: 'Beneficio' },
  { value: 'cobertura', label: 'Cobertura' },
  { value: 'exclusion', label: 'Exclusión' },
  { value: 'queCubre', label: '¿Qué Cubre?' },
  { value: 'queNoCubre', label: '¿Qué No Cubre?' },
  { value: 'vigencia', label: 'Vigencia' }
];

// Función para obtener un color según el tipo de item

export default function PlanesForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = Boolean(id);
  
  const { getListRoute } = useContextualNavigation();
  const planesListRoute = getListRoute('planes');
  
  const [familias, setFamilias] = useState([]);
  const [urls, setUrls] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [portalData, setPortalData] = useState({ plan: [] });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archived, setArchived] = useState(false);
  const [itemGroups, setItemGroups] = useState([]);
  const [badges, setBadges] = useState([]);
  const [showArrayModal, setShowArrayModal] = useState(false);
  const [planArray, setPlanArray] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);

  // Función para generar el array del plan
  const generatePlanArray = () => {
    const formData = watch();
    
    // Convertir itemGroups a array plano
    const flatItems = [];
    itemGroups.forEach(group => {
      group.items.forEach(item => {
        flatItems.push({
          titulo: group.titulo,
          tipo: group.tipo,
          item: item.item
        });
      });
    });

    const planArrayData = {
      uuid: formData.uuid || id,
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      urlImagen: formData.urlImagen,
      badgeDestacador: formData.badgeDestacador,
      status: formData.status,
      fkFam: formData.fkFam,
      fkUrl: formData.fkUrl,
      nota: formData.nota,
      urlContratacion: formData.urlContratacion,
      landbotConfig: formData.landbotConfig,
      es_complementario: formData.es_complementario || false,
      plan_base_id: formData.plan_base_id || '',
      archived: archived ? 1 : 0,
      items: flatItems
    };

    return planArrayData;
  };

  // Función para mostrar el array del plan
  const handleShowPlanArray = () => {
    const arrayData = generatePlanArray();
    setPlanArray(arrayData);
    setShowArrayModal(true);
  };

  // Función para copiar el array al portapapeles
  const handleCopyArray = async () => {
    try {
      const arrayString = JSON.stringify(planArray, null, 2);
      await navigator.clipboard.writeText(arrayString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
      setError('Error al copiar al portapapeles');
    }
  };

  // Load badges from localStorage
  useEffect(() => {
    const storedBadges = safeParseJson(safeLocalStorage.getItem('planBadges'), []);
    setBadges(storedBadges);
  }, []);

  const getTipoColor = (tipo) => {
    const colorMap = {
      'titulo': '#3b82f6',     // blue-500
      'queEs': '#158B5C',      // emerald-500
      'beneficio': '#6366f1',  // indigo-500
      'cobertura': '#f59e0b',  // amber-500
      'exclusion': '#ef4444',  // red-500
      'queCubre': '#8b5cf6',   // violet-500
      'queNoCubre': '#f43f5e', // rose-500
      'vigencia': '#0ea5e9'    // sky-500
    };
    
    return colorMap[tipo] || '#6b7280'; // gray-500 as default
  };
  
  
  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      nombre: '',
      descripcion: '',
      urlImagen: '',
      badgeDestacador: '',
      status: 'active',
      fkFam: '',
      fkUrl: '',
      nota: '',
      urlContratacion: '',
      landbotConfig: '',
      es_complementario: false,
      plan_base_id: '',
      items: [{ titulo: '', tipo: 'titulo', item: '' }]
    }
  });
  
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  // Watch for image URL changes
  const watchedImageUrl = watch("urlImagen");
  
  useEffect(() => {
    if (watchedImageUrl) {
      setImagePreview(watchedImageUrl);
    }
  }, [watchedImageUrl]);

  // Cargar datos iniciales (familias, URLs y planes)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [familiasData, urlsData, planesData] = await Promise.all([
          getFamilias(),
          getUrls(),
          getPlanes()
        ]);
        setFamilias(familiasData);
        setUrls(urlsData);
        setPlanes(planesData || []);
      } catch (err) {
        console.error('Error al cargar datos iniciales:', err);
        setError('Error al cargar datos iniciales. Por favor, intente nuevamente.');
      }
    };
    
    loadInitialData();
  }, []);

  // Cargar datos del plan si estamos en modo edición
  useEffect(() => {
    if (id) {
      const loadPlan = async () => {
        try {
          setLoading(true);
          const planData = await getPlanById(id);
          
          // Group items by tipo and titulo
          const groups = {};
          const formattedItems = planData.items?.map(item => {
            const formattedItem = {
              recordId: item.recordId || '',
              titulo: item.titulo || '',
              tipo: item.tipo || 'titulo',
              item: item.item || ''
            };
            
            // Create a key for grouping
            const groupKey = `${formattedItem.tipo}-${formattedItem.titulo}`;
            if (!groups[groupKey]) {
              groups[groupKey] = {
                tipo: formattedItem.tipo,
                titulo: formattedItem.titulo,
                items: []
              };
            }
            groups[groupKey].items.push(formattedItem);
            
            return formattedItem;
          }) || [];
          
          setItemGroups(Object.values(groups));
          
          // Preparar los datos del formulario con valores por defecto para campos nuevos
          const formDataToReset = {
            ...planData,
            es_complementario: planData?.es_complementario === true ? true : false,
            plan_base_id: planData?.plan_base_id ? String(planData.plan_base_id) : '',
            items: formattedItems.length > 0 ? formattedItems : [{ titulo: '', tipo: 'titulo', item: '' }]
          };
          
          console.log('� PLAN CARGADO:', { 
            nombre: planData.nombre,
            es_complementario: formDataToReset.es_complementario, 
            plan_base_id: formDataToReset.plan_base_id,
            rawEsComplementario: planData?.es_complementario
          });
          
          reset(formDataToReset);
          
          // Set archived state based on the plan data
          setArchived(planData.archived === 1 || planData.archived === '1' || planData.archived === true);
          
          setImagePreview(planData.urlImagen || "");
          if (planData.portal && planData.portal.plan) {
            setPortalData({ plan: planData.portal.plan });
          }
        } catch (err) {
          console.error('Error al cargar el plan:', err);
          setError('Error al cargar el plan. Por favor, intente nuevamente.');
        } finally {
          setLoading(false);
        }
      };
      
      loadPlan();
    }
  }, [id, reset]);

  // New function to add a group
  const handleAddGroup = () => {
    const newGroup = {
      tipo: 'titulo',
      titulo: '',
      items: [{ item: '' }]
    };
    setItemGroups([...itemGroups, newGroup]);
  };

  // New function to add an item to a group
  const handleAddItemToGroup = (groupIndex) => {
    const updatedGroups = [...itemGroups];
    updatedGroups[groupIndex].items.push({ item: '' });
    setItemGroups(updatedGroups);
  };

  // New function to remove a group
  const handleRemoveGroup = (groupIndex) => {
    const updatedGroups = [...itemGroups];
    updatedGroups.splice(groupIndex, 1);
    setItemGroups(updatedGroups);
  };

  // New function to update group title
  const handleGroupTitleChange = (groupIndex, value) => {
    const updatedGroups = [...itemGroups];
    updatedGroups[groupIndex].titulo = value;
    setItemGroups(updatedGroups);
  };

  // New function to update group type
  const handleGroupTypeChange = (groupIndex, value) => {
    const updatedGroups = [...itemGroups];
    updatedGroups[groupIndex].tipo = value;
    setItemGroups(updatedGroups);
  };

  // New function to update item content
  const handleItemContentChange = (groupIndex, itemIndex, value) => {
    const updatedGroups = [...itemGroups];
    updatedGroups[groupIndex].items[itemIndex].item = value;
    setItemGroups(updatedGroups);
  };

  // Functions for handling item deletion
  const handleDeleteItem = (item) => {
    console.log("Item to delete:", item);
    setItemToDelete(item);
    setDeleteItemDialogOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;
    
    try {
      setLoading(true);
      console.log("Deleting item with recordId:", itemToDelete.recordId);
      
      // Prevent form submission during item deletion
      
      await deletePlanItem(itemToDelete.recordId);
      
      // Update local state by removing the deleted item
      const updatedPortalData = {
        ...portalData,
        plan: portalData.plan.filter(item => item.recordId !== itemToDelete.recordId)
      };
      setPortalData(updatedPortalData);
      
      // Also update itemGroups to reflect the deletion
      const updatedGroups = [...itemGroups];
      for (let i = 0; i < updatedGroups.length; i++) {
        const group = updatedGroups[i];
        const itemIndex = group.items.findIndex(item => item.recordId === itemToDelete.recordId);
        if (itemIndex !== -1) {
          group.items.splice(itemIndex, 1);
          if (group.items.length === 0) {
            updatedGroups.splice(i, 1);
          }
          break;
        }
      }
      setItemGroups(updatedGroups);
      
      setDeleteItemDialogOpen(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error deleting plan item:", error);
      setError("Error al eliminar el ítem del plan. Por favor, intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Replace the handleRemoveItemFromGroup function with this version
  const handleRemoveItemFromGroup = (groupIndex, itemIndex) => {
    
    const updatedGroups = [...itemGroups];
    const itemToRemove = updatedGroups[groupIndex].items[itemIndex];
    
    // If the item has a recordId (exists in database), show confirmation dialog
    if (itemToRemove.recordId) {
      // Prepare the item for the confirmation dialog
      const itemForDialog = {
        recordId: itemToRemove.recordId,
        titulo: updatedGroups[groupIndex].titulo,
        tipo: updatedGroups[groupIndex].tipo,
        item: itemToRemove.item
      };
      
      // Show confirmation dialog
      setItemToDelete(itemForDialog);
      setDeleteItemDialogOpen(true);
    } else {
      // If it's a new item without recordId, just remove it from the array immediately
      updatedGroups[groupIndex].items.splice(itemIndex, 1);
      
      // If no items left, remove the group
      if (updatedGroups[groupIndex].items.length === 0) {
        updatedGroups.splice(groupIndex, 1);
      }
      
      setItemGroups(updatedGroups);
    }
  };

  // Modified onSubmit to handle grouped items and deletions
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      // Asegurarse de que el plan tenga un UUID si es nuevo
      if (!id && !data.uuid) {
        data.uuid = uuidv4();
      }
      
      // Convert grouped items to flat items array for API
      const flatItems = [];
      itemGroups.forEach(group => {
        group.items.forEach(item => {
          // Include all items, even deleted ones
          const itemData = {
            recordId: item.recordId || '',
            titulo: group.titulo,
            tipo: group.tipo,
            item: item.item,
            modified: true
          };
          
          // If item is marked for deletion, add a delete flag
          if (item.deleted) {
            itemData.deleted = true;
          }
          
          flatItems.push(itemData);
        });
      });
      
      console.log('Items payload:', flatItems);
      
      if (id) {
        // Actualizar plan existente
        const originalData = await getPlanById(id);
        const changedData = {};
        
        // Compare and only include changed fields
        if (data.nombre !== originalData.nombre) changedData.nombre = data.nombre;
        if (data.descripcion !== originalData.descripcion) changedData.descripcion = data.descripcion;
        if (data.urlImagen !== originalData.urlImagen) changedData.urlImagen = data.urlImagen;
        if (data.badgeDestacador !== originalData.badgeDestacador) changedData.badgeDestacador = data.badgeDestacador;
        if (data.status !== originalData.status) changedData.status = data.status;
        if (data.fkFam !== originalData.fkFam) changedData.fkFam = data.fkFam;
        if (data.fkUrl !== originalData.fkUrl) changedData.fkUrl = data.fkUrl;
        if (data.nota !== originalData.nota) changedData.nota = data.nota;
        if (data.urlContratacion !== originalData.urlContratacion) changedData.urlContratacion = data.urlContratacion;
        if (data.landbotConfig !== originalData.landbotConfig) changedData.landbotConfig = data.landbotConfig;
        if (data.es_complementario !== originalData.es_complementario) changedData.es_complementario = data.es_complementario;
        if (data.plan_base_id !== originalData.plan_base_id) changedData.plan_base_id = data.plan_base_id;
        
        // Make sure to pass the flatItems array as the second parameter
        await updatePlan(id, changedData, flatItems);
      } else {
        // Crear nuevo plan
        await createPlan({
          nombre: data.nombre,
          descripcion: data.descripcion,
          urlImagen: data.urlImagen,
          badgeDestacador: data.badgeDestacador,
          status: data.status,
          fkFam: data.fkFam,
          fkUrl: data.fkUrl,
          nota: data.nota,
          urlContratacion: data.urlContratacion || '',
          landbotConfig: data.landbotConfig || '',
          es_complementario: data.es_complementario || false,
          plan_base_id: data.plan_base_id || '',
          uuid: data.uuid
        }, flatItems);
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate(planesListRoute);
      }, 2000);
    } catch (err) {
      console.error('Error al guardar el plan:', err);
      setError('Error al guardar el plan. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    append({ titulo: '', tipo: 'titulo', item: '' });
  };

  if (loading && id) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6"> {/* Added p-6 padding */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{id ? 'Editar' : 'Nuevo'} Plan</h1>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert>
          <AlertDescription>
            {id ? 'Plan actualizado correctamente' : 'Plan creado correctamente'}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre</Label>
              <Input
                id="nombre"
                {...register('nombre', { required: 'El nombre es obligatorio' })}
                placeholder="Nombre del plan"
              />
              {errors.nombre && (
                <p className="text-sm text-destructive">{errors.nombre.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                {...register('descripcion')}
                placeholder="Descripción del plan"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg border border-blue-200">
              {console.log('✅ SWITCH RENDERIZADO - es_complementario:', watch('es_complementario'))}
              <div className="flex-1">
                <Label htmlFor="es_complementario" className="text-sm font-medium cursor-pointer">
                  {watch('es_complementario') ? "Plan Complementario" : "Plan Principal"}
                </Label>
                <p className="text-xs text-gray-600 mt-1">
                  {watch('es_complementario') ? "Este plan amplia la cobertura de otro plan" : "Este es un plan base"}
                </p>
              </div>
              <Switch
                id="es_complementario"
                checked={watch('es_complementario') || false}
                onCheckedChange={(checked) => {
                  console.log('✅ Toggle es_complementario:', checked);
                  setValue('es_complementario', checked);
                  if (!checked) {
                    setValue('plan_base_id', '');
                  }
                }}
              />
            </div>

            {watch('es_complementario') && (
              <div className="space-y-2 mt-2">
                <Label htmlFor="plan_base_id">¿Amplia cuál plan?</Label>
                <Select 
                  onValueChange={value => {
                    console.log('✅ Select plan_base_id:', value);
                    setValue('plan_base_id', value);
                  }}
                  value={watch('plan_base_id') || ""}
                  className="bg-white"
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Seleccione el plan base" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300">
                    <SelectItem value="">Ninguno</SelectItem>
                    {planes
                      .filter(plan => plan.uuid !== id && plan.recordId !== id)
                      .map((plan) => (
                        <SelectItem key={plan.recordId || plan.uuid} value={plan.recordId || plan.uuid}>
                          {plan.nombre}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="urlImagen">URL de la Imagen</Label>
              <Input
                id="urlImagen"
                {...register('urlImagen')}
                placeholder="https://yooldigital.com/assets/default.png"
              />
            </div>

    
            
            <div className="space-y-2">
              <Label htmlFor="badgeDestacador">Badge Destacador</Label>
              <Select 
                onValueChange={value => setValue('badgeDestacador', value)}
                value={watch('badgeDestacador') || ""}
                className="bg-white"
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Seleccione un badge" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-300">
                  <SelectItem value="">Ninguno</SelectItem>
                  {badges.map((badge) => (
                    <SelectItem key={badge.id} value={badge.name}>
                      <div className="flex items-center">
                        <span 
                          className="inline-block w-3 h-3 rounded-full mr-2" 
                          style={{ backgroundColor: badge.color }}
                        ></span>
                        {badge.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fkFam">Familia</Label>
                <Select 
                  onValueChange={value => setValue('fkFam', value)}
                  value={watch('fkFam')}
                  className="bg-white" // Added background color class
                >
                  <SelectTrigger className="bg-white"> {/* Added background color class */}
                    <SelectValue placeholder="Seleccione familia" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300"> {/* Added background and border */}
                    <SelectItem value="">Ninguna</SelectItem>
                    {familias.map((familia) => (
                      <SelectItem key={familia.uuid} value={familia.uuid}>
                        {familia.nombre || familia.titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fkUrl">URL</Label>
                <Select 
                  onValueChange={value => setValue('fkUrl', value)}
                  value={watch('fkUrl')}
                  className="bg-white" // Added background color class
                >
                  <SelectTrigger className="bg-white"> {/* Added background color class */}
                    <SelectValue placeholder="Seleccione URL" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-300"> {/* Added background and border */}
                    <SelectItem value="">Ninguna</SelectItem>
                    {urls.map((url) => (
                      <SelectItem key={url.uuid} value={url.uuid}>
                        {url.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

  <div className="grid grid-cols-2 gap-4">
    <div className="space-y-2">
      <Label htmlFor="urlContratacion">URL de Contratación</Label>
      <Input
        id="urlContratacion"
        {...register('urlContratacion')}
        placeholder="/ylmt"
        disabled={!!watch('fkUrl')}
        className={watch('fkUrl') ? "bg-gray-100" : ""}
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="landbotConfig">Configuración de Landbot</Label>
      <Input
        id="landbotConfig"
        {...register('landbotConfig')}
        placeholder="rc"
        disabled={!!watch('fkUrl')}
        className={watch('fkUrl') ? "bg-gray-100" : ""}
      />
    </div>
  </div>

            <div className="space-y-2">
              <Label htmlFor="nota">Nota (Disclaimer Legal)</Label>
              <Textarea
                id="nota"
                {...register('nota')}
                placeholder="Información legal o disclaimer del plan"
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2 pt-4">
              <Switch
                id="status"
                checked={watch('status') === 'active'}
                onCheckedChange={(checked) => {
                  setValue('status', checked ? 'active' : 'inactive');
                }}
              />
              <Label htmlFor="status" className="cursor-pointer">
                {watch('status') === 'active' ? "Activo" : "Inactivo"}
              </Label>
            </div>
          </div>

          <div>
            <Card className="overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                    src={imagePreview || "https://yooldigital.com/assets/default.png"} 
                    alt="Vista previa" 
                    className="w-full h-48 object-cover object-top rounded-md"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://yooldigital.com/assets/default.png";
                    }}
                  />
                {watch('badgeDestacador') && (
                  <div className="absolute top-2 right-2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {watch('badgeDestacador')}
                  </div>
                )}
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent p-4">
                  <h3 className="text-white text-xl font-bold">{watch('nombre') || 'Nombre del Plan'}</h3>
                  <p className="text-white/80 text-sm line-clamp-2">{watch('descripcion') || 'Descripción del plan'}</p>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="text-lg font-semibold">Vista Previa</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Así se verá la tarjeta del plan en el listado
                </p>
                {watch('fkFam') && (
                  <div className="mt-2">
                    <span className="text-sm font-medium">Familia: </span>
                    <span className="text-sm text-gray-600">
                      {familias.find(f => f.uuid === watch('fkFam'))?.nombre || 
                       familias.find(f => f.uuid === watch('fkFam'))?.titulo || 
                       'No seleccionada'}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <Separator className="my-6" />
        
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Detalles del Plan</h3>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddGroup}
              className="flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Sección
            </Button>
          </div>

          {/* Two-column layout for editor and preview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column: Grouped items editor */}
            <div className="space-y-6">
              {itemGroups.map((group, groupIndex) => (
                <Card 
                  key={groupIndex} 
                  className="mb-6 border-l-4" 
                  style={{ borderLeftColor: getTipoColor(group.tipo) }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        <div className="space-y-2">
                          <Label>Título de la Sección</Label>
                          <Input
                            value={group.titulo}
                            onChange={(e) => handleGroupTitleChange(groupIndex, e.target.value)}
                            placeholder="Ej: Cobertura, Beneficios, etc."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tipo de Sección</Label>
                          <Select 
                            onValueChange={(value) => handleGroupTypeChange(groupIndex, value)}
                            value={group.tipo}
                            className="bg-white"
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Seleccione tipo" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border border-gray-300">
                              {tiposItem.map((tipo) => (
                                <SelectItem key={tipo.value} value={tipo.value}>
                                  {tipo.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveGroup(groupIndex)}
                        className="ml-2"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {group.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center space-x-2">
                          <Input
                            value={item.item}
                            onChange={(e) => handleItemContentChange(groupIndex, itemIndex, e.target.value)}
                            placeholder="Contenido del ítem"
                            className="flex-1"
                          />
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveItemFromGroup(groupIndex, itemIndex);
                            }}
                            className="ml-2"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Eliminar
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddItemToGroup(groupIndex)}
                        className="mt-2"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Ítem
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Right column: Preview of the plan structure */}
            <div className="space-y-6">
              {itemGroups.length > 0 && (
                <Card className="bg-gray-50 h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Vista Previa de Estructura</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="modal-content-wrapper border rounded-md p-4 bg-white">
                      <h2 className="modal-title text-xl font-bold mb-4">{watch('nombre') || 'Nombre del Plan'}</h2>
                      
                      {/* Map through specific sections in the correct order */}
                      {/* ¿Qué es? Section */}
                      {itemGroups.find(g => g.tipo === 'queEs') && (
                        <section className="modal-section mb-4 pb-2 border-b border-gray-200">
                          <h3 className="font-medium text-lg mb-2">¿Qué es?</h3>
                          <p className="text-gray-700">
                            {itemGroups.find(g => g.tipo === 'queEs').items.map(item => item.item).join(' ')}
                          </p>
                        </section>
                      )}
                      
                      {/* ¿Qué cubre? Section */}
                      {itemGroups.find(g => g.tipo === 'queCubre') && (
                        <section className="modal-section mb-4 pb-2 border-b border-gray-200">
                          <h3 className="font-medium text-lg mb-2">¿Qué cubre?</h3>
                          <ul className="pl-5">
                            {itemGroups.find(g => g.tipo === 'queCubre').items.map((item, itemIndex) => (
                              <li key={itemIndex} className="list-disc text-gray-700 mb-1">
                                {item.item || 'Sin contenido'}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}
                      
                      {/* ¿Qué no cubre? Section */}
                      {itemGroups.find(g => g.tipo === 'queNoCubre') && (
                        <section className="modal-section mb-4 pb-2 border-b border-gray-200">
                          <h3 className="font-medium text-lg mb-2">¿Qué no cubre?</h3>
                          <ul className="not-covered-list pl-5">
                            {itemGroups.find(g => g.tipo === 'queNoCubre').items.map((item, itemIndex) => (
                              <li key={itemIndex} className="list-disc text-gray-700 mb-1">
                                {item.item || 'Sin contenido'}
                              </li>
                            ))}
                          </ul>
                        </section>
                      )}
                      
                      {/* Vigencia Section */}
                      {itemGroups.find(g => g.tipo === 'vigencia') && (
                        <section className="modal-section mb-4 pb-2 border-b border-gray-200">
                          <h3 className="font-medium text-lg mb-2">Vigencia</h3>
                          <p className="text-gray-700">
                            {itemGroups.find(g => g.tipo === 'vigencia').items.map(item => item.item).join(' ')}
                          </p>
                        </section>
                      )}
                      
                      {/* Other sections (coberturas, beneficios, etc.) */}
                      {itemGroups.filter(g => !['queEs', 'queCubre', 'queNoCubre', 'vigencia'].includes(g.tipo)).map((group, groupIndex) => (
                        <section key={groupIndex} className="modal-section mb-4 pb-2 border-b border-gray-200 last:border-b-0">
                          <h3 className="font-medium text-lg mb-2">{group.titulo}</h3>
                          
                          {/* For title type, just show the content */}
                          {group.tipo === 'titulo' && (
                            <div className="font-medium">
                              {group.items.map((item, itemIndex) => (
                                <p key={itemIndex} className="text-gray-700">
                                  {item.item || 'Sin contenido'}
                                </p>
                              ))}
                            </div>
                          )}
                          
                          {/* For list content types */}
                          {['beneficio', 'cobertura', 'exclusion'].includes(group.tipo) && (
                            <ul className={group.tipo === 'exclusion' ? 'not-covered-list pl-5' : 'coverage-list pl-5'}>
                              {group.items.map((item, itemIndex) => (
                                <li key={itemIndex} className="list-disc text-gray-700 mb-1">
                                  {item.item || 'Sin contenido'}
                                </li>
                              ))}
                            </ul>
                          )}
                        </section>
                      ))}
                      
                      {/* Note/Disclaimer section */}
                      {watch('nota') && (
                        <div className="modal-note mt-4 p-3 bg-gray-100 rounded-md text-sm text-gray-600">
                          <p>{watch('nota')}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* CTA Button Preview */}
                    <div className="mt-4 flex justify-center">
                      <button 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md"
                        type="button"
                        onClick={(e) => e.preventDefault()}
                      >
                        Contratar Ahora
                      </button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

       {/* Archive toggle section (only for edit mode) */}
       {id && (
          <div className="border-t pt-6 mt-6">
            <h2 className="text-xl font-semibold mb-4">Estado de Archivo</h2>
            <div className="flex items-center space-x-4">
              <Switch 
                checked={archived} 
                onCheckedChange={async (newState) => {
                  try {
                    setLoading(true);
                    // Convert boolean to numeric value (1 or 0) for FileMaker
                    await updatePlan(id, { archived: newState ? 1 : 0 });
                    setArchived(newState);
                    setSuccess(true);
                    setTimeout(() => setSuccess(false), 3000);
                  } catch (err) {
                    console.error('Error al actualizar estado de archivo:', err);
                    setError('Error al actualizar estado de archivo. Por favor, intente nuevamente.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              />
              <div>
                <p className="font-medium">{archived ? 'Archivado' : 'No Archivado'}</p>
                <p className="text-sm text-gray-500">
                  {archived 
                    ? 'Este plan está archivado y no aparecerá en las listas principales.' 
                    : 'Este plan está activo y visible en las listas principales.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone (only for edit mode) */}
        {id && (
          <div className="border-t border-red-200 pt-6 mt-6">
            <h2 className="text-xl font-semibold text-[var(--yool-error)] mb-4">Zona de Peligro</h2>
            <p className="text-sm text-gray-500 mb-4">
              Las siguientes acciones son irreversibles. Por favor, tenga cuidado.
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-50"
              onClick={() => {
                setDeleteDialogOpen(true);
              }}
            >
              Eliminar Plan Permanentemente
            </Button>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(planesListRoute)}
          >
            Cancelar
          </Button>
          {id && (
            <Button
              type="button"
              variant="outline"
              onClick={handleShowPlanArray}
              className="border-blue-500 text-blue-500 hover:bg-blue-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              Ver Plan Array
            </Button>
          )}
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Guardando...' : id ? 'Actualizar Plan' : 'Crear Plan'}
          </Button>
        </div>
      </form>

      {/* Modal para mostrar el array del plan */}
      <AlertDialog open={showArrayModal} onOpenChange={setShowArrayModal}>
        <AlertDialogContent className="bg-white rounded-2xl max-w-4xl max-h-[80vh] overflow-hidden">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <Eye className="w-5 h-5 mr-2" />
              Array del Plan - {watch('nombre')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Este es el array completo del plan con todos sus datos y elementos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <div className="mb-4 flex justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleCopyArray}
                className={`${copySuccess ? 'border-green-500 text-green-500' : 'border-blue-500 text-blue-500'} hover:bg-blue-50`}
              >
                {copySuccess ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    ¡Copiado!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Array
                  </>
                )}
              </Button>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 overflow-auto max-h-96">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(planArray, null, 2)}
              </pre>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cerrar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog for item deletion */}
      <AlertDialog open={deleteItemDialogOpen} onOpenChange={setDeleteItemDialogOpen}>
        <AlertDialogContent className="bg-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de borrar este ítem?
              <div className="mt-2 p-2 bg-gray-100 rounded">
                <p><strong>Tipo:</strong> {itemToDelete?.tipo}</p>
                <p><strong>Título:</strong> {itemToDelete?.titulo}</p>
                <p><strong>Contenido:</strong> {itemToDelete?.item}</p>
              </div>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                confirmDeleteItem();
              }} 
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete plan confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el plan 
              "{watch('nombre')}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-[#8E8E93] border-[#8E8E93] hover:bg-gray-50">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                try {
                  setLoading(true);
                  await deletePlan(id);
                  navigate(planesListRoute);
                } catch (err) {
                  console.error("Error deleting plan:", err);
                  setError("No se pudo eliminar el plan");
                  setLoading(false);
                }
              }} 
              className="bg-[#FF3B30] hover:bg-[#D70015] text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} // Add this closing curly brace to close the PlanesForm function
